import React from 'react';
import { clsx } from 'clsx';
import { Icon, ExpandToggleIcon } from '@/components/icons';
import {
  MODAL_BODY_CLASS,
  MODAL_CLOSE_ICON_CLASS,
  MODAL_FOOTER_CLASS,
  MODAL_HEADER_CLASS,
  MODAL_OVERLAY_CLASS,
  MODAL_PANEL_CLASS,
  SECTION_TITLE_CLASS,
  PANEL_SEPARATOR_B,
} from '@/styles/classes';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '818';
  panelClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
  hideHeader?: boolean;
  closeAriaLabel?: string;
  disabled?: boolean;
}

const maxWidthClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-4xl',
  '3xl': 'max-w-3xl',
  '818': 'max-w-[818px]',
};

export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  children,
  footer,
  maxWidth = 'md',
  panelClassName,
  bodyClassName,
  footerClassName,
  hideHeader = false,
  closeAriaLabel = 'Close',
  disabled = false,
}) => {
  if (!open) {
    return null;
  }

  return (
    <div className={MODAL_OVERLAY_CLASS} onClick={onClose}>
      <div
        className={clsx(MODAL_PANEL_CLASS, maxWidthClasses[maxWidth], panelClassName)}
        onClick={event => event.stopPropagation()}
      >
        {hideHeader ? null : (
          <div className={MODAL_HEADER_CLASS}>
            <h2 className={SECTION_TITLE_CLASS}>{title}</h2>
            <button
              type="button"
              onClick={onClose}
              className="flex size-6 items-center justify-center"
              disabled={disabled}
              aria-label={closeAriaLabel}
            >
              <Icon icon="xmark" className={MODAL_CLOSE_ICON_CLASS} />
            </button>
          </div>
        )}

        <div className={clsx(MODAL_BODY_CLASS, 'overflow-y-auto', bodyClassName)}>
          {children}
        </div>

        {footer ? (
          <div className={clsx(MODAL_FOOTER_CLASS, footerClassName)}>{footer}</div>
        ) : null}
      </div>
    </div>
  );
};

type CollapsibleSectionProps = {
  title: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  contentClassName?: string;
};

export function CollapsibleSection({
  title,
  expanded,
  onToggle,
  children,
  contentClassName,
}: CollapsibleSectionProps) {
  return (
    <section className={clsx(PANEL_SEPARATOR_B, 'px-8 py-4')}>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between text-left"
        aria-expanded={expanded}
      >
        <span className="text-base font-semibold text-unilink-text-primary">{title}</span>
        <ExpandToggleIcon expanded={expanded} />
      </button>
      {expanded ? <div className={clsx('mt-4', contentClassName)}>{children}</div> : null}
    </section>
  );
}
